import React, { useCallback, useState, useRef } from 'react';
import './DatingCanvas.css';
import { ProfileCard } from '../types';
import ProfileTileDisplay from './ProfileTileDisplay';
import InteractionManager, { InteractionType, GameState } from '../data/interactionTypes';
import { searchService } from '../lib/searchIntegration';
import { useAnalytics } from '../lib/analytics/client';

interface DatingCanvasProps {
  selectedCard: ProfileCard | null;
  onClose: () => void;
  initialText?: string;
  onCardUpdate?: (card: ProfileCard) => void;
}

interface StickyNote {
  id: string;
  x: number;
  y: number;
  text: string;
  color: string;
  parentId?: string; // For conversation threading
}

interface AIResponse {
  id: string;
  x: number;
  y: number;
  text: string;
  isLoading?: boolean;
  parentId: string; // ID of the sticky note that triggered this response
  character: 'universe' | 'nagpal'; // Which character is responding
  position: 'top' | 'bottom' | 'new-card'; // Where on the card to position
  interactionType?: InteractionType;
  gameState?: GameState;
  searchContext?: string;
}

interface Connection {
  id: string;
  fromId: string;
  toId: string;
  fromType: 'sticky' | 'ai' | 'profile';
  toType: 'sticky' | 'ai' | 'profile';
}

const DatingCanvas: React.FC<DatingCanvasProps> = ({ selectedCard, onClose, initialText }) => {
  const analytics = useAnalytics();
  console.log('🎯 Canvas: Component initialized, analytics:', !!analytics);
  const [stickyNotes, setStickyNotes] = useState<StickyNote[]>([]);
  const [aiResponses, setAiResponses] = useState<AIResponse[]>([]);
  const [connections, setConnections] = useState<Connection[]>([]);
  // Mobile-aware profile card positioning
  const getInitialProfileCardPosition = () => {
    const isMobile = window.innerWidth <= 768;
    return {
      x: isMobile ? 20 : 0,   // Slightly inset on mobile
      y: isMobile ? 20 : 0    // Small top margin on mobile
    };
  };
  
  const [profileCardPosition, setProfileCardPosition] = useState(getInitialProfileCardPosition());
  const [interactionManager] = useState(() => new InteractionManager());
  const [currentInteraction, setCurrentInteraction] = useState<GameState | null>(null);
  const canvasRef = useRef<HTMLDivElement>(null);
  
  // Zoom state
  const [scale, setScale] = useState(1);
  const [isPinching, setIsPinching] = useState(false);
  const lastTouchDistance = useRef<number | null>(null);
  
  // Info popup state
  const [showInfoPopup, setShowInfoPopup] = useState(false);
  
  // Mobile vertical layout helper
  const getNextMobilePosition = useCallback((parentElement: { x: number; y: number }, elementType: 'ai' | 'sticky' = 'ai') => {
    const isMobile = window.innerWidth <= 768;
    
    console.log('🔥 MOBILE DETECTION - getNextMobilePosition:', { 
      windowWidth: window.innerWidth, 
      isMobile, 
      elementType,
      parentElement 
    });
    
    if (!isMobile) {
      // Desktop: horizontal layout (to the right)
      console.log('🔥 DESKTOP MODE - positioning horizontally');
      return {
        x: parentElement.x + 250,
        y: parentElement.y
      };
    }
    
    // Mobile: vertical layout (below)
    const verticalOffset = elementType === 'ai' ? 120 : 100; // AI cards slightly more space
    console.log('🔥 MOBILE MODE - positioning vertically with offset:', verticalOffset);
    return {
      x: parentElement.x + 10, // Small horizontal offset for visual clarity
      y: parentElement.y + verticalOffset
    };
  }, []);
  
  // Backend configuration state
  const [universeEnabled, setUniverseEnabled] = useState(false);
  
  // Canvas panning state
  const [canvasOffset, setCanvasOffset] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });
  const [lastPanPosition, setLastPanPosition] = useState({ x: 0, y: 0 });
  
  // Analytics

  // Get names from user data  
  const anshikaName = "Anshika"; // Primary character name
  const visitorName = "Visitor"; // Generic visitor name
  
  // Fetch backend configuration on component mount
  React.useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await fetch('/api/config');
        if (response.ok) {
          const config = await response.json();
          setUniverseEnabled(config.universeEnabled || false);
        }
      } catch (error) {
        console.error('Failed to fetch configuration:', error);
        // Default to disabled if fetch fails
        setUniverseEnabled(false);
      }
    };
    
    fetchConfig();
  }, []);

  // Get conversation context for a given note
  const getConversationContext = useCallback((noteId: string): string[] => {
    const context: string[] = [];
    
    // Find the sticky note
    const note = stickyNotes.find(n => n.id === noteId);
    if (note) {
      // If this note is connected to profile card, add that context
      if (note.parentId === 'profile' && selectedCard) {
        context.push(`Context: User is responding to your "${selectedCard.title || selectedCard.type}" section`);
      }
      
      context.push(`User: ${note.text}`);
      
      // Find AI responses to this note
      const responses = aiResponses.filter(r => r.parentId === noteId && !r.isLoading);
      responses.forEach(response => {
        context.push(`Anshika: ${response.text}`);
      });
    }
    
    return context;
  }, [stickyNotes, aiResponses, selectedCard]);

  const handleCanvasDoubleClick = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    if (e.target === e.currentTarget) {
      const rect = e.currentTarget.getBoundingClientRect();
      let clientX, clientY;
      
      // Handle both mouse and touch events
      if ('clientX' in e) {
        clientX = e.clientX;
        clientY = e.clientY;
      } else {
        clientX = e.touches[0]?.clientX || 0;
        clientY = e.touches[0]?.clientY || 0;
      }
      
      const x = clientX - rect.left - 100;
      const y = clientY - rect.top - 60;
      
      const newNote: StickyNote = {
        id: `note_${Date.now()}`,
        x,
        y,
        text: 'Type your thoughts...',
        color: ['yellow', 'blue', 'pink', 'green'][Math.floor(Math.random() * 4)],
        parentId: selectedCard ? 'profile' : undefined // Connect to profile card if present
      };
      
      setStickyNotes(prev => [...prev, newNote]);
      
      // Track sticky note creation
      console.log('🎯 Canvas: About to track note creation, analytics:', !!analytics);
      if (analytics) {
        console.log('🎯 Canvas: Tracking note create:', newNote.id, { x, y }, newNote.text);
        analytics.trackNoteCreate(newNote.id, { x, y }, newNote.text);
      } else {
        console.error('❌ Canvas: Analytics not available!');
      }
      
      // Create connection from profile card to sticky note if profile card exists
      if (selectedCard) {
        const profileConnection: Connection = {
          id: `conn_profile_${Date.now()}`,
          fromId: 'profile',
          toId: newNote.id,
          fromType: 'profile',
          toType: 'sticky'
        };
        
        setConnections(prev => [...prev, profileConnection]);
      }
    }
  }, [selectedCard, analytics]);

  // Canvas panning handlers
  const handleCanvasPanStart = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    // Only pan if clicking on the canvas background, not on elements
    if (e.target !== e.currentTarget) return;
    
    let clientX, clientY;
    if ('clientX' in e) {
      clientX = e.clientX;
      clientY = e.clientY;
    } else {
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
      
      // Add haptic feedback for touch panning
      if (navigator.vibrate) {
        navigator.vibrate([5]); // Very short vibration for pan start
      }
    }
    
    setIsPanning(true);
    setPanStart({ x: clientX, y: clientY });
    setLastPanPosition({ x: clientX, y: clientY });
  }, []);

  const handleCanvasPanMove = useCallback((e: MouseEvent | TouchEvent) => {
    if (!isPanning) return;
    
    let clientX, clientY;
    if ('clientX' in e) {
      clientX = e.clientX;
      clientY = e.clientY;
    } else {
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
    }
    
    const deltaX = clientX - lastPanPosition.x;
    const deltaY = clientY - lastPanPosition.y;
    
    setCanvasOffset(prev => ({
      x: prev.x + deltaX,
      y: prev.y + deltaY
    }));
    
    setLastPanPosition({ x: clientX, y: clientY });
  }, [isPanning, lastPanPosition]);

  const handleCanvasPanEnd = useCallback(() => {
    setIsPanning(false);
  }, []);

  // Pinch-to-zoom handlers
  const getTouchDistance = (touches: React.TouchList | TouchList) => {
    if (touches.length < 2) return null;
    const touch1 = touches[0];
    const touch2 = touches[1];
    return Math.sqrt(
      Math.pow(touch2.clientX - touch1.clientX, 2) + 
      Math.pow(touch2.clientY - touch1.clientY, 2)
    );
  };

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    if (e.touches.length === 2) {
      setIsPinching(true);
      lastTouchDistance.current = getTouchDistance(e.touches);
    }
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (isPinching && e.touches.length === 2) {
      e.preventDefault();
      const currentDistance = getTouchDistance(e.touches);
      
      if (lastTouchDistance.current && currentDistance) {
        const scaleChange = currentDistance / lastTouchDistance.current;
        setScale(prevScale => {
          const newScale = prevScale * scaleChange;
          return Math.min(Math.max(newScale, 0.5), 3); // Limit zoom between 0.5x and 3x
        });
        lastTouchDistance.current = currentDistance;
      }
    }
  }, [isPinching]);

  const handleTouchEnd = useCallback((e: React.TouchEvent) => {
    if (e.touches.length < 2) {
      setIsPinching(false);
      lastTouchDistance.current = null;
    }
  }, []);

  // Add pan event listeners
  React.useEffect(() => {
    if (isPanning) {
      document.addEventListener('mousemove', handleCanvasPanMove);
      document.addEventListener('mouseup', handleCanvasPanEnd);
      document.addEventListener('touchmove', handleCanvasPanMove, { passive: false });
      document.addEventListener('touchend', handleCanvasPanEnd);
      
      return () => {
        document.removeEventListener('mousemove', handleCanvasPanMove);
        document.removeEventListener('mouseup', handleCanvasPanEnd);
        document.removeEventListener('touchmove', handleCanvasPanMove);
        document.removeEventListener('touchend', handleCanvasPanEnd);
      };
    }
  }, [isPanning, handleCanvasPanMove, handleCanvasPanEnd]);

  const handleNoteSubmit = useCallback(async (noteId: string, text: string) => {
    if (!text.trim()) {
      console.log('HandleNoteSubmit: Empty text, returning early');
      return;
    }
    
    console.log('HandleNoteSubmit called with:', { noteId, text });
    
    // Find the sticky note position
    const stickyNote = stickyNotes.find(note => note.id === noteId);
    if (!stickyNote) {
      console.log('HandleNoteSubmit: Sticky note not found for ID:', noteId);
      return;
    }
    
    // Determine interaction type based on text content and context
    const interactionType = determineInteractionType(text, selectedCard);
    console.log('Determined interaction type:', interactionType);
    
    // Check if we should start a new interaction/game - only if Universe is enabled
    let gameState = currentInteraction;
    if (universeEnabled && (!gameState || !gameState.active)) {
      gameState = interactionManager.startInteraction(interactionType, ['universe', 'visitor']);
      setCurrentInteraction(gameState);
    } else if (!universeEnabled) {
      // Clear game state when Universe is disabled
      gameState = null;
      setCurrentInteraction(null);
    }
    
    // Get search context if needed
    let searchContext = '';
    if (text.includes('restaurant') || text.includes('food') || text.includes('date') || text.includes('plan')) {
      try {
        const suggestions = await searchService.getQuickSuggestions(text);
        searchContext = suggestions.join('; ');
      } catch (error) {
        console.log('Search service error:', error);
      }
    }
    
    // Character selection: Always use Anshika when Universe is disabled
    const isGameInteraction = universeEnabled && [
      InteractionType.THIS_OR_THAT,
      InteractionType.ASSUMPTIONS, 
      InteractionType.PLAN_DATE,
      InteractionType.PLAN_TRIP,
      InteractionType.UNIVERSE_INTRO,
      InteractionType.ICEBREAKER
    ].includes(interactionType);
    
    // Always use 'nagpal' (Anshika) when Universe is disabled
    const character = (universeEnabled && isGameInteraction) ? 'universe' : 'nagpal';
    
    console.log(`Character responding: ${character}`);
    
    // Create AI response with mobile-aware positioning - VERTICAL STACK ON MOBILE
    const responseId = `${character}_${Date.now()}`;
    const aiPosition = getNextMobilePosition(stickyNote, 'ai');
    
    console.log('🔥 AI RESPONSE - PARENT NOTE:', { x: stickyNote.x, y: stickyNote.y });
    console.log('🔥 AI RESPONSE - NEW POSITION:', aiPosition);
    
    const loadingResponse: AIResponse = {
      id: responseId,
      x: aiPosition.x,
      y: aiPosition.y,
      text: '',
      isLoading: true,
      parentId: noteId,
      character,
      position: 'new-card',
      interactionType,
      gameState,
      searchContext
    };
    
    setAiResponses(prev => [...prev, loadingResponse]);
    
    // Create connection
    const connection: Connection = {
      id: `conn_${character}_${Date.now()}`,
      fromId: noteId,
      toId: responseId,
      fromType: 'sticky',
      toType: 'ai'
    };
    setConnections(prev => [...prev, connection]);
    
    try {
      const conversationContext = getConversationContext(noteId);
      const characterName = character === 'universe' ? 'Universe' : anshikaName;
      const responseText = await generateCharacterResponse(
        text,
        selectedCard,
        conversationContext,
        character,
        characterName,
        visitorName,
        false,
        interactionType,
        gameState,
        searchContext,
        false // No longer using shouldNagpalRespond logic
      );
      
      console.log(`Received response text: "${responseText}"`);
      
      // Update response
      setAiResponses(prev => prev.map(response => 
        response.id === responseId 
          ? { ...response, text: responseText, isLoading: false }
          : response
      ));
      
      // Update game state if needed - only when Universe is enabled
      if (universeEnabled && gameState && interactionType !== InteractionType.UNIVERSE_INTRO) {
        interactionManager.updateGameState({ round: gameState.round + 1 });
        setCurrentInteraction(interactionManager.getCurrentState());
      }
      
      console.log('AI response updated successfully');
      
      // Track AI interaction
      console.log('🎯 Canvas: About to track AI call, analytics:', !!analytics);
      if (analytics) {
        console.log('🎯 Canvas: Tracking AI call for note:', noteId);
        const startTime = Date.now();
        analytics.trackAICall({
          noteId,
          conversationId: `conv_${stickyNote.parentId || noteId}`,
          position: { x: loadingResponse.x, y: loadingResponse.y },
          character: character === 'nagpal' ? 'human' : character as 'human' | 'universe',
          userText: text,
          profileContext: selectedCard,
          conversationContext,
          isLimitReached: false,
          fullPrompt: `User message: ${text}`, // Simplified prompt tracking
          aiResponse: responseText,
          humanName: character === 'universe' ? 'Universe' : anshikaName,
          visitorName,
          interactionCount: conversationContext.length + 1,
          responseTime: Date.now() - startTime,
          success: true,
          triggerCard: selectedCard?.id
        });
      }
      
    } catch (error) {
      console.error(`${character} response error:`, error);
      console.error('Full error details:', {
        message: error.message,
        stack: error.stack,
        character,
        noteId,
        text
      });
      setAiResponses(prev => prev.filter(response => response.id !== responseId));
      setConnections(prev => prev.filter(c => c.id !== connection.id));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stickyNotes, selectedCard, currentInteraction, interactionManager, anshikaName, visitorName, getConversationContext]);


  const handleNoteDelete = useCallback((noteId: string) => {
    setStickyNotes(prev => prev.filter(note => note.id !== noteId));
    // Also remove related AI responses and connections
    setAiResponses(prev => prev.filter(response => response.parentId !== noteId));
    setConnections(prev => prev.filter(c => c.toId !== noteId && c.fromId !== noteId));
  }, []);

  const handleNoteUpdate = useCallback((noteId: string, newText: string) => {
    setStickyNotes(prev => prev.map(note => {
      if (note.id === noteId) {
        // Track note edit
        if (analytics) {
          analytics.trackNoteEdit(noteId, { x: note.x, y: note.y }, newText);
        }
        return { ...note, text: newText };
      }
      return note;
    }));
  }, [analytics]);

  // Update sticky note positions only (AI responses are now independent)
  const handleNotePositionUpdate = useCallback((noteId: string, newPosition: { x: number; y: number }) => {
    setStickyNotes(prev => prev.map(note => 
      note.id === noteId ? { ...note, x: newPosition.x, y: newPosition.y } : note
    ));
    // AI responses are now independent and don't move with their parent sticky notes
  }, []);

  // Update AI response positions when they are dragged
  const handleAIResponsePositionUpdate = useCallback((responseId: string, newPosition: { x: number; y: number }) => {
    setAiResponses(prev => prev.map(response => 
      response.id === responseId ? { ...response, x: newPosition.x, y: newPosition.y } : response
    ));
  }, []);

  // Handle continuing conversation from AI response
  const handleContinueConversation = useCallback((aiResponseId: string, fromPosition: { x: number; y: number }) => {
    // Create a new sticky note positioned near the AI response (mobile-aware)
    const continuePosition = getNextMobilePosition(fromPosition, 'sticky');
    
    console.log('🔥 CONTINUE - FROM AI RESPONSE:', fromPosition);
    console.log('🔥 CONTINUE - NEW NOTE POSITION:', continuePosition);
    
    const newStickyNote: StickyNote = {
      id: `note_continue_${Date.now()}`,
      x: continuePosition.x,
      y: continuePosition.y,
      text: 'Ask them something more or share your thoughts...',
      color: ['yellow', 'blue', 'pink', 'green'][Math.floor(Math.random() * 4)],
      parentId: aiResponseId // Link to the specific AI response being continued
    };

    setStickyNotes(prev => [...prev, newStickyNote]);
    
    // Track continuation
    if (analytics) {
      analytics.trackNoteContinue(aiResponseId, newStickyNote.id, newStickyNote);
    }

    // Create connection from AI response to new sticky note
    const connection: Connection = {
      id: `conn_continue_${Date.now()}`,
      fromId: aiResponseId, // Connect from the specific AI response card
      toId: newStickyNote.id,
      fromType: 'ai',
      toType: 'sticky'
    };

    setConnections(prev => [...prev, connection]);
  }, [analytics, getNextMobilePosition]);

  // Function to start specific interactions
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const startSpecificInteraction = useCallback((type: InteractionType) => {
    console.log('Starting specific interaction:', type);
    
    // End current interaction if any
    if (currentInteraction && currentInteraction.active) {
      interactionManager.endInteraction();
    }
    
    // Start new interaction
    const newGameState = interactionManager.startInteraction(type, ['universe', 'visitor', 'nagpal']);
    setCurrentInteraction(newGameState);
    
    // Create Universe prompt for this interaction type
    const universePromptId = `universe_prompt_${Date.now()}`;
    const universePrompt: AIResponse = {
      id: universePromptId,
      x: 300 + (aiResponses.length * 50),
      y: 150 + (aiResponses.length * 30),
      text: '',
      isLoading: true,
      parentId: 'interaction_start',
      character: 'universe',
      position: 'new-card',
      interactionType: type,
      gameState: newGameState
    };
    
    setAiResponses(prev => [...prev, universePrompt]);
    
    // Generate Universe interaction starter
    generateCharacterResponse(
      `Start ${type} interaction`,
      selectedCard,
      [],
      'universe',
      'Universe',
      visitorName,
      false,
      type,
      newGameState
    ).then(promptText => {
      setAiResponses(prev => prev.map(response => 
        response.id === universePromptId 
          ? { ...response, text: promptText, isLoading: false }
          : response
      ));
    }).catch(error => {
      console.error('Universe prompt error:', error);
      setAiResponses(prev => prev.filter(r => r.id !== universePromptId));
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentInteraction, interactionManager, selectedCard, visitorName, aiResponses.length]);

  // Initialize with a user-friendly sticky note when card is selected
  React.useEffect(() => {
    if ((selectedCard || initialText) && stickyNotes.length === 0) {
      const noteText = initialText || 
              (selectedCard 
                ? (universeEnabled 
                    ? `What catches your eye about ${selectedCard.title || 'this'}? Share your thoughts...`
                    : `Tell Anshika what you think about ${selectedCard.title || 'this'}...`)
                : (universeEnabled 
                    ? 'Double-click anywhere to start a conversation...'
                    : 'Double-click to chat with Anshika...'));
      
      // Mobile-aware initial note positioning
      const isMobile = window.innerWidth <= 768;
      console.log('🔥 INITIAL NOTE - Mobile detection:', { windowWidth: window.innerWidth, isMobile });
      
      const initialNote: StickyNote = {
        id: 'initial_note',
        x: isMobile ? 50 : (selectedCard ? 400 : 300),  // Start at left edge on mobile
        y: isMobile ? 80 : 150,                         // Higher position on mobile (below header)
        text: noteText,
        color: 'yellow',
        parentId: selectedCard ? 'profile' : undefined
      };
      
      console.log('🔥 INITIAL NOTE - Position:', { x: initialNote.x, y: initialNote.y, isMobile });
      setStickyNotes([initialNote]);
      
      // Track initial note creation
      if (analytics) {
        console.log('🎯 Canvas: Tracking initial note creation');
        analytics.trackNoteCreate(initialNote.id, { x: initialNote.x, y: initialNote.y }, initialNote.text);
      }
      
      // Create initial connection from profile card to sticky note
      if (selectedCard) {
        const initialConnection: Connection = {
          id: 'conn_initial',
          fromId: 'profile',
          toId: 'initial_note',
          fromType: 'profile',
          toType: 'sticky'
        };
        setConnections([initialConnection]);
      }
    }
  }, [selectedCard, initialText, stickyNotes.length, universeEnabled, analytics]);

  return (
    <div className="dating-canvas">
      <div className="canvas-header">
        <h1 className="canvas-title">
          🌌 {selectedCard ? `${selectedCard.title || selectedCard.type} - Cosmic Canvas` : 'Cosmic Connection Space'}
        </h1>
        <div className="canvas-tools">
          <button 
            className="info-btn-header" 
            onClick={() => setShowInfoPopup(true)}
            title="Help & Tips"
          >
            ℹ️ Help
          </button>
          <button className="canvas-close" onClick={onClose}>← Close</button>
        </div>
      </div>
      
      <div 
        className="canvas-workspace-custom" 
        ref={canvasRef}
        onDoubleClick={handleCanvasDoubleClick}
        onMouseDown={handleCanvasPanStart}
        onTouchStart={(e) => {
          // Handle pinch-to-zoom first
          handleTouchStart(e);
          
          // Handle double tap on mobile and panning
          if (e.target !== e.currentTarget) return;
          
          const now = Date.now();
          const lastTap = (e.currentTarget as any).lastTap || 0;
          const tapDelay = now - lastTap;
          
          if (tapDelay < 300 && tapDelay > 50) {
            // Double tap detected - create note
            e.preventDefault();
            e.stopPropagation();
            handleCanvasDoubleClick(e);
          } else if (tapDelay > 300 && e.touches.length === 1) {
            // Single tap - start panning (only if not pinching)
            handleCanvasPanStart(e);
          }
          (e.currentTarget as any).lastTap = now;
        }}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        style={{
          touchAction: 'none', // Prevent default touch behaviors
          WebkitTouchCallout: 'none', // Disable iOS callout menu
          WebkitUserSelect: 'none', // Disable text selection
          userSelect: 'none',
          cursor: isPanning ? 'grabbing' : 'grab'
        }}
      >
        {/* Canvas Content Container with offset */}
        <div
          className={`canvas-content ${isPanning ? 'panning' : ''}`}
          style={{
            transform: `translate(${canvasOffset.x}px, ${canvasOffset.y}px) scale(${scale})`,
            transformOrigin: 'center',
            width: '200vw',
            height: '200vh',
            position: 'relative'
          }}
        >
        {/* Draggable Profile Tile Display */}
        {selectedCard && (
          <DraggableProfileTile 
            card={selectedCard} 
            position={profileCardPosition}
            onPositionChange={setProfileCardPosition}
          />
        )}
        
        {/* Sticky Notes */}
        {stickyNotes.map(note => (
          <StickyNoteComponent
            key={note.id}
            note={note}
            onSubmit={handleNoteSubmit}
            onDelete={handleNoteDelete}
            onUpdate={handleNoteUpdate}
            onPositionUpdate={handleNotePositionUpdate}
          />
        ))}
        
        {/* AI Response Cards */}
        {aiResponses.map(response => (
          <AIResponseCard
            key={response.id}
            response={response}
            onDelete={(id) => {
              setAiResponses(prev => prev.filter(r => r.id !== id));
              setConnections(prev => prev.filter(c => c.toId !== id && c.fromId !== id));
            }}
            onPositionUpdate={handleAIResponsePositionUpdate}
            onContinueConversation={handleContinueConversation}
          />
        ))}
        
        {/* Game State Display - Only show when Universe is enabled */}
        {universeEnabled && currentInteraction && currentInteraction.active && (
          <div className="game-state-display">
            <div className="game-info">
              🎮 <strong>{currentInteraction.type.replace('_', ' ').toUpperCase()}</strong>
              {currentInteraction.maxRounds && (
                <span className="round-counter">
                  Round {currentInteraction.round}/{currentInteraction.maxRounds}
                </span>
              )}
              {currentInteraction.score && (
                <span className="score">
                  Matches: {currentInteraction.score.visitor + currentInteraction.score.nagpal}
                </span>
              )}
            </div>
          </div>
        )}
        
        {/* Mobile Instructions */}
        {typeof window !== 'undefined' && window.innerWidth <= 768 && (
          <div className="mobile-instructions">
            <div className="instruction-text">
              {stickyNotes.length === 0 ? (
                <>
                  📱 <strong>Double-tap</strong> to create a note<br />
                  👆 <strong>Touch & drag</strong> notes to move<br />
                  🤏 <strong>Drag background</strong> to pan around
                </>
              ) : (
                <>
                  ✍️ <strong>Tap note</strong> to edit text<br />
                  💬 <strong>Press Enter</strong> to get AI response<br />
                  ➕ <strong>Tap +</strong> to continue conversation
                </>
              )}
            </div>
          </div>
        )}
        
        {/* Mobile Gesture Helper */}
        {typeof window !== 'undefined' && window.innerWidth <= 768 && (
          <div className="mobile-gesture-helper">
            <div className="gesture-hint">
              {isPanning ? '🤏 Panning...' : '👆 Touch to interact'}
            </div>
          </div>
        )}
        
        {/* Connection Lines */}
        <svg 
          className="connection-overlay"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            pointerEvents: 'none',
            zIndex: 1
          }}
        >
          {connections.map(connection => (
            <ConnectionLine
              key={connection.id}
              connection={connection}
              stickyNotes={stickyNotes}
              aiResponses={aiResponses}
              profileCardPosition={profileCardPosition}
            />
          ))}
        </svg>
        
        {/* Game Instructions Note - Only show when Universe is enabled */}
        {universeEnabled && (
          <div className="game-instructions-note">
            <div className="game-note-content">
              <div className="game-note-header">🎮 Want to play games?</div>
              <div className="game-triggers">
                Try saying: <strong>"this or that"</strong> • <strong>"I assume you..."</strong> • <strong>"plan a date"</strong> • <strong>"play a game"</strong>
              </div>
            </div>
          </div>
        )}

        {/* Permanent Usage Instructions Sticky Note */}
        <div className="permanent-instructions-note">
          <div className="instructions-content">
            💡 {universeEnabled 
              ? 'Press Enter after writing • Best used in Desktop mode' 
              : 'Chat directly with Anshika • Press Enter to send'}
          </div>
        </div>
        
        {/* Mobile Floating Action Button */}
        <button 
          className="mobile-fab"
          onClick={(e) => {
            e.stopPropagation();
            // Create note at center of visible area
            const viewportCenterX = window.innerWidth / 2;
            const viewportCenterY = window.innerHeight / 2;
            handleCanvasDoubleClick({
              clientX: viewportCenterX,
              clientY: viewportCenterY - 100, // Account for header
              preventDefault: () => {}
            } as React.MouseEvent);
          }}
          title="Add Note"
        >
          +
        </button>

        {/* Zoom Indicator */}
        {scale !== 1 && (
          <div className="zoom-indicator">
            {Math.round(scale * 100)}%
          </div>
        )}

        {/* Info Popup */}
        {showInfoPopup && (
          <div className="info-popup-overlay" onClick={() => setShowInfoPopup(false)}>
            <div className="info-popup" onClick={(e) => e.stopPropagation()}>
              <div className="info-popup-header">
                <h3>🌌 Canvas Guide</h3>
                <button 
                  className="info-popup-close" 
                  onClick={() => setShowInfoPopup(false)}
                >
                  ×
                </button>
              </div>
              
              <div className="info-popup-content">
                <div className="info-section">
                  <h4>📱 Mobile Tips</h4>
                  <ul>
                    <li><strong>Double-tap</strong> background to create notes</li>
                    <li><strong>Touch & drag</strong> to move notes around</li>
                    <li><strong>Tap Enter</strong> to get AI responses</li>
                    <li><strong>Pinch</strong> to zoom in/out</li>
                    <li><strong>Drag background</strong> to pan around</li>
                    <li><strong>Tap +</strong> to continue conversations</li>
                  </ul>
                </div>
                
                <div className="info-section">
                  <h4>💻 Desktop Tips</h4>
                  <ul>
                    <li><strong>Double-click</strong> background to create notes</li>
                    <li><strong>Click & drag</strong> to move elements</li>
                    <li><strong>Type & press Enter</strong> for AI responses</li>
                    <li><strong>Drag background</strong> to pan around</li>
                    <li><strong>Use +</strong> button to continue chats</li>
                  </ul>
                </div>
                
                <div className="info-section">
                  <h4>💬 Conversation Tips</h4>
                  <ul>
                    <li>Be <strong>specific</strong> about what interests you</li>
                    <li>Ask <strong>questions</strong> to keep the chat going</li>
                    <li>Cards stack <strong>vertically on mobile</strong></li>
                    <li>Each note gets its own <strong>AI response</strong></li>
                    <li>Delete notes with <strong>×</strong> button</li>
                  </ul>
                </div>
                
                <div className="info-section">
                  <h4>🎨 Organization</h4>
                  <ul>
                    <li>Notes have <strong>different colors</strong> randomly</li>
                    <li><strong>Drag to organize</strong> your thoughts</li>
                    <li>AI responses appear <strong>below on mobile</strong></li>
                    <li>Lines show <strong>conversation flow</strong></li>
                  </ul>
                </div>
              </div>
              
              <div className="info-popup-footer">
                <button 
                  className="info-popup-got-it" 
                  onClick={() => setShowInfoPopup(false)}
                >
                  Got it! 
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Analytics Debug Indicator */}
        {analytics && (
          <div style={{
            position: 'fixed',
            bottom: '20px',
            left: '20px',
            background: 'rgba(0, 255, 0, 0.1)',
            border: '1px solid rgba(0, 255, 0, 0.3)',
            padding: '10px',
            borderRadius: '5px',
            fontSize: '12px',
            color: '#00ff00',
            fontFamily: 'monospace',
            zIndex: 9999
          }}>
            📊 Canvas Analytics Active<br/>
            Session: {analytics.getSessionId().substring(0, 15)}...
          </div>
        )}
        
        </div> {/* Close canvas-content */}
      </div> {/* Close canvas-workspace-custom */}
    </div>
  );
};

// Custom Sticky Note Component
interface StickyNoteProps {
  note: StickyNote;
  onSubmit: (id: string, text: string) => void;
  onDelete: (id: string) => void;
  onUpdate: (id: string, text: string) => void;
  onPositionUpdate: (id: string, position: { x: number; y: number }) => void;
}

const StickyNoteComponent: React.FC<StickyNoteProps> = ({ note, onSubmit, onDelete, onUpdate, onPositionUpdate }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [position, setPosition] = useState({ x: note.x, y: note.y });
  const [text, setText] = useState(note.text);
  // Check if this is a default placeholder text (not user-entered content)
  const isDefaultPlaceholder = 
    note.text === 'Ask them something more or share your thoughts...' || 
    note.text.includes('What catches your eye about') ||
    note.text === 'Double-click anywhere to start a conversation...';
    
  console.log('StickyNote: note.text =', note.text, 'isDefaultPlaceholder =', isDefaultPlaceholder);
    
  const [isPlaceholderVisible, setIsPlaceholderVisible] = useState(isDefaultPlaceholder);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (e.target === textareaRef.current) return;
    
    e.preventDefault();
    setIsDragging(true);
    setDragOffset({
      x: e.clientX - position.x,
      y: e.clientY - position.y
    });
  }, [position]);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    if (e.target === textareaRef.current) return;
    
    // Prevent default touch behaviors like scrolling
    e.preventDefault();
    e.stopPropagation();
    
    // Add haptic feedback for touch devices
    if (navigator.vibrate) {
      navigator.vibrate([10]); // Short vibration for better feedback
    }
    
    setIsDragging(true);
    setDragOffset({
      x: e.touches[0].clientX - position.x,
      y: e.touches[0].clientY - position.y
    });
    
    // Enhanced visual feedback for touch
    const element = e.currentTarget as HTMLElement;
    element.style.transform = 'translateY(-4px) scale(1.05)';
    element.style.zIndex = '1000';
    element.style.boxShadow = '0 8px 24px rgba(0, 0, 0, 0.6)';
    element.style.borderWidth = '3px';
    
    // Add touch class for better styling
    element.classList.add('touch-active');
  }, [position]);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isDragging) return;
    
    const newPosition = {
      x: e.clientX - dragOffset.x,
      y: e.clientY - dragOffset.y
    };
    
    setPosition(newPosition);
    onPositionUpdate(note.id, newPosition);
  }, [isDragging, dragOffset, note.id, onPositionUpdate]);

  const handleTouchMove = useCallback((e: TouchEvent) => {
    if (!isDragging) return;
    
    const newPosition = {
      x: e.touches[0].clientX - dragOffset.x,
      y: e.touches[0].clientY - dragOffset.y
    };
    
    setPosition(newPosition);
    onPositionUpdate(note.id, newPosition);
  }, [isDragging, dragOffset, note.id, onPositionUpdate]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    
    // Reset visual feedback after drag ends
    setTimeout(() => {
      const elements = document.querySelectorAll('.custom-sticky-note');
      elements.forEach(el => {
        const element = el as HTMLElement;
        element.style.transform = '';
        element.style.zIndex = '';
        element.style.boxShadow = '';
        element.style.borderWidth = '';
        element.classList.remove('touch-active');
      });
    }, 100);
  }, []);

  React.useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.addEventListener('touchmove', handleTouchMove, { passive: false });
      document.addEventListener('touchend', handleMouseUp);
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.removeEventListener('touchmove', handleTouchMove);
        document.removeEventListener('touchend', handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleTouchMove, handleMouseUp]);

  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newText = e.target.value;
    setText(newText);
    onUpdate(note.id, newText);
    
    // Hide placeholder when user starts typing
    if (isPlaceholderVisible && newText.length > 0) {
      setIsPlaceholderVisible(false);
    }
  };

  const handleFocus = () => {
    // Clear placeholder text when user focuses
    if (isPlaceholderVisible) {
      setText('');
      setIsPlaceholderVisible(false);
    }
  };

  const handleSubmit = () => {
    // Check if this is actually user-entered content (not placeholder)
    const isUserContent = !isPlaceholderVisible && text.trim() && 
                         !text.includes('What catches your eye') && 
                         !text.includes('Double-click anywhere') &&
                         !text.includes('Ask them something more');
    
    if (isUserContent) {
      console.log('StickyNote: Submitting text:', text);
      onSubmit(note.id, text);
    } else {
      console.log('StickyNote: Ignoring placeholder text submission');
    }
  };

  return (
    <div
      className={`custom-sticky-note ${note.color}`}
      style={{
        left: position.x,
        top: position.y,
        cursor: isDragging ? 'grabbing' : 'grab'
      }}
      onMouseDown={handleMouseDown}
      onTouchStart={handleTouchStart}
    >
      <textarea
        ref={textareaRef}
        className="sticky-textarea"
        value={isPlaceholderVisible ? text : text}
        onChange={handleTextChange}
        onFocus={handleFocus}
        placeholder={isPlaceholderVisible ? "" : "Type your thoughts..."}
        style={{ 
          color: isPlaceholderVisible ? 'rgba(255, 255, 255, 0.5)' : 'rgba(255, 255, 255, 0.9)',
          fontStyle: isPlaceholderVisible ? 'italic' : 'normal'
        }}
        onClick={(e) => e.stopPropagation()}
      />
      <div className="sticky-actions">
        <button className="sticky-enter-btn" onClick={handleSubmit}>
          Enter
        </button>
        <button className="sticky-delete-btn" onClick={() => onDelete(note.id)}>
          ×
        </button>
      </div>
    </div>
  );
};

// Draggable Profile Tile Component
interface DraggableProfileTileProps {
  card: ProfileCard;
  position: { x: number; y: number };
  onPositionChange: (position: { x: number; y: number }) => void;
}

const DraggableProfileTile: React.FC<DraggableProfileTileProps> = ({ card, position, onPositionChange }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
    setDragOffset({
      x: e.clientX - position.x,
      y: e.clientY - position.y
    });
  }, [position]);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    e.preventDefault();
    setIsDragging(true);
    setDragOffset({
      x: e.touches[0].clientX - position.x,
      y: e.touches[0].clientY - position.y
    });
  }, [position]);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isDragging) return;
    
    onPositionChange({
      x: e.clientX - dragOffset.x,
      y: e.clientY - dragOffset.y
    });
  }, [isDragging, dragOffset, onPositionChange]);

  const handleTouchMove = useCallback((e: TouchEvent) => {
    if (!isDragging) return;
    
    onPositionChange({
      x: e.touches[0].clientX - dragOffset.x,
      y: e.touches[0].clientY - dragOffset.y
    });
  }, [isDragging, dragOffset, onPositionChange]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  React.useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.addEventListener('touchmove', handleTouchMove, { passive: false });
      document.addEventListener('touchend', handleMouseUp);
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.removeEventListener('touchmove', handleTouchMove);
        document.removeEventListener('touchend', handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleTouchMove, handleMouseUp]);

  return (
    <div
      className={`draggable-profile-tile ${isDragging ? 'dragging' : ''}`}
      style={{
        left: position.x,
        top: position.y,
        cursor: isDragging ? 'grabbing' : 'grab'
      }}
      onMouseDown={handleMouseDown}
      onTouchStart={handleTouchStart}
    >
      <ProfileTileDisplay card={card} />
    </div>
  );
};

// AI Response Card Component
interface AIResponseCardProps {
  response: AIResponse;
  onDelete: (id: string) => void;
  onPositionUpdate: (id: string, position: { x: number; y: number }) => void;
  onContinueConversation: (parentId: string, fromPosition: { x: number; y: number }) => void;
}

const AIResponseCard: React.FC<AIResponseCardProps> = ({ response, onDelete, onPositionUpdate, onContinueConversation }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [position, setPosition] = useState({ x: response.x, y: response.y });

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    // Don't drag when clicking on buttons
    if (e.target instanceof HTMLButtonElement) return;
    
    e.preventDefault();
    setIsDragging(true);
    setDragOffset({
      x: e.clientX - position.x,
      y: e.clientY - position.y
    });
  }, [position]);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    // Don't drag when touching buttons
    if (e.target instanceof HTMLButtonElement) return;
    
    e.preventDefault();
    setIsDragging(true);
    setDragOffset({
      x: e.touches[0].clientX - position.x,
      y: e.touches[0].clientY - position.y
    });
  }, [position]);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isDragging) return;
    
    const newPosition = {
      x: e.clientX - dragOffset.x,
      y: e.clientY - dragOffset.y
    };
    
    setPosition(newPosition);
    onPositionUpdate(response.id, newPosition);
  }, [isDragging, dragOffset, response.id, onPositionUpdate]);

  const handleTouchMove = useCallback((e: TouchEvent) => {
    if (!isDragging) return;
    
    const newPosition = {
      x: e.touches[0].clientX - dragOffset.x,
      y: e.touches[0].clientY - dragOffset.y
    };
    
    setPosition(newPosition);
    onPositionUpdate(response.id, newPosition);
  }, [isDragging, dragOffset, response.id, onPositionUpdate]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  React.useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.addEventListener('touchmove', handleTouchMove, { passive: false });
      document.addEventListener('touchend', handleMouseUp);
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.removeEventListener('touchmove', handleTouchMove);
        document.removeEventListener('touchend', handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleTouchMove, handleMouseUp]);

  const getCharacterStyling = () => {
    if (response.character === 'universe') {
      return {
        className: 'ai-response-card universe-card',
        badge: '🌌 UNIVERSE',
        emoji: '🌌',
        loadingText: 'Orchestrating magic...'
      };
    } else {
      return {
        className: 'ai-response-card nagpal-card',
        badge: '👩‍💻 Anshika',
        emoji: '🐼',
        loadingText: 'Thinking...'
      };
    }
  };

  const styling = getCharacterStyling();

  return (
    <div
      className={`${styling.className} ${isDragging ? 'dragging' : ''}`}
      style={{
        left: position.x,
        top: position.y,
        cursor: isDragging ? 'grabbing' : 'grab'
      }}
      onMouseDown={handleMouseDown}
      onTouchStart={handleTouchStart}
    >
      {response.isLoading ? (
        <div className="ai-loading">
          <div className="panda-animation">{styling.emoji}</div>
          <div className="loading-text">{styling.loadingText}</div>
        </div>
      ) : (
        <>
          <div className={`ai-response-text ${response.character}-text`}>
            {response.text}
          </div>
          <div className="ai-response-actions">
            <span className={`ai-badge ${response.character}-badge`}>
              {styling.badge}
            </span>
            <div className="ai-action-buttons">
              <button 
                className="ai-continue-btn" 
                onClick={() => onContinueConversation(response.id, position)}
                title="Continue conversation"
              >
                +
              </button>
              <button className="ai-delete-btn" onClick={() => onDelete(response.id)}>
                ×
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// Helper function to determine interaction type based on user input
const determineInteractionType = (text: string, profileCard: ProfileCard | null): InteractionType => {
  const lowerText = text.toLowerCase();
  
  if (lowerText.includes('plan') && (lowerText.includes('date') || lowerText.includes('meet'))) {
    return InteractionType.PLAN_DATE;
  }
  if (lowerText.includes('this or that') || lowerText.includes('choose') || lowerText.includes('prefer')) {
    return InteractionType.THIS_OR_THAT;
  }
  if (lowerText.includes('assume') || lowerText.includes('guess') || lowerText.includes('think you')) {
    return InteractionType.ASSUMPTIONS;
  }
  if (lowerText.includes('question') || lowerText.includes('ask')) {
    return InteractionType.CUSTOM_QUESTION;
  }
  if (lowerText.includes('game') || lowerText.includes('play')) {
    return InteractionType.THIS_OR_THAT; // Default game
  }
  
  return InteractionType.CUSTOM_QUESTION; // Default to simple conversation
};

// Note: Removed shouldNagpalJumpIn logic - Anshika responds to everything now

// Character Response Generation Function
const generateCharacterResponse = async (
  userText: string, 
  profileCard: ProfileCard | null, 
  conversationContext: string[] = [],
  character: 'universe' | 'nagpal',
  humanName: string,
  visitorName: string,
  isLimitReached: boolean = false,
  interactionType?: InteractionType,
  gameState?: GameState | null,
  searchContext?: string,
  nagpalShouldRespond?: boolean
): Promise<string> => {
  console.log(`Making API request for ${character} character...`);

  try {
    const response = await fetch('/api/claude', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        userText,
        profileCard,
        conversationContext,
        character,
        humanName,
        visitorName,
        isLimitReached,
        interactionType,
        gameState,
        searchContext,
        nagpalShouldRespond
      })
    });

    console.log(`${character} API response status:`, response.status);

    if (!response.ok) {
      const errorText = await response.text();
      console.error('API Error Response:', errorText);
      throw new Error(`API error: ${response.status} - ${errorText}`);
    }

    const data = await response.json();
    console.log(`${character} API Response data:`, data);
    return data.text;
  } catch (error) {
    console.error(`${character} response generation failed:`, error);
    throw error;
  }
};


// Connection Line Component
interface ConnectionLineProps {
  connection: Connection;
  stickyNotes: StickyNote[];
  aiResponses: AIResponse[];
  profileCardPosition: { x: number; y: number };
}

const ConnectionLine: React.FC<ConnectionLineProps> = ({ 
  connection, 
  stickyNotes, 
  aiResponses, 
  profileCardPosition 
}) => {
  // Get positions of connected elements - this will automatically update when elements move
  const getElementPosition = (id: string, type: 'sticky' | 'ai' | 'profile') => {
    switch (type) {
      case 'sticky':
        const stickyNote = stickyNotes.find(n => n.id === id);
        return stickyNote ? { x: stickyNote.x + 100, y: stickyNote.y + 40 } : null; // Center of sticky note
      case 'ai':
        const aiResponse = aiResponses.find(r => r.id === id);
        return aiResponse ? { x: aiResponse.x + 100, y: aiResponse.y + 30 } : null; // Center of AI card
      case 'profile':
        // Profile card position - right edge for outgoing connections
        return { x: profileCardPosition.x + 300, y: profileCardPosition.y + 100 }; // Right edge of profile tile
      default:
        return null;
    }
  };

  const fromPos = getElementPosition(connection.fromId, connection.fromType);
  const toPos = getElementPosition(connection.toId, connection.toType);

  if (!fromPos || !toPos) return null;

  // Calculate arrow path with slight curve
  const dx = toPos.x - fromPos.x;
  const dy = toPos.y - fromPos.y;
  const midX = fromPos.x + dx * 0.5;
  const midY = fromPos.y + dy * 0.5;
  const controlX = midX + (dy * 0.1); // Slight curve perpendicular to line
  const controlY = midY - (dx * 0.1);

  const pathData = `M ${fromPos.x} ${fromPos.y} Q ${controlX} ${controlY} ${toPos.x} ${toPos.y}`;

  // Calculate arrow head
  const angle = Math.atan2(dy, dx);
  const arrowLength = 12;
  const arrowAngle = Math.PI / 6; // 30 degrees

  const arrowX1 = toPos.x - arrowLength * Math.cos(angle - arrowAngle);
  const arrowY1 = toPos.y - arrowLength * Math.sin(angle - arrowAngle);
  const arrowX2 = toPos.x - arrowLength * Math.cos(angle + arrowAngle);
  const arrowY2 = toPos.y - arrowLength * Math.sin(angle + arrowAngle);

  // Different colors for different connection types
  const getConnectionColor = () => {
    if (connection.fromType === 'profile') {
      return { main: 'rgba(76, 175, 80, 0.8)', glow: 'rgba(76, 175, 80, 0.3)' }; // Green for profile connections
    } else if (connection.fromType === 'sticky' && connection.toType === 'ai') {
      return { main: 'rgba(102, 126, 234, 0.8)', glow: 'rgba(102, 126, 234, 0.3)' }; // Purple for sticky→AI
    }
    return { main: 'rgba(102, 126, 234, 0.6)', glow: 'rgba(102, 126, 234, 0.2)' }; // Default
  };

  const colors = getConnectionColor();

  return (
    <g className={`connection-line connection-${connection.fromType}-to-${connection.toType}`}>
      {/* Glow effect */}
      <path
        d={pathData}
        stroke={colors.glow}
        strokeWidth="8"
        fill="none"
        className="connection-glow"
      />
      
      {/* Main line */}
      <path
        d={pathData}
        stroke={colors.main}
        strokeWidth="2"
        fill="none"
        strokeDasharray={connection.fromType === 'profile' ? '8,4' : '5,5'}
        className="connection-path"
      />
      
      {/* Arrow head */}
      <polygon
        points={`${toPos.x},${toPos.y} ${arrowX1},${arrowY1} ${arrowX2},${arrowY2}`}
        fill={colors.main}
        className="connection-arrow"
      />
    </g>
  );
};

export default DatingCanvas;